import { createMirrorBrainActions } from '../../supabase/functions/_shared/mirrorBrainActions';
import type { BrainNoteProposalArgs, BrainHandoffArgs } from '../../supabase/functions/_shared/brainIntent';
import { noteOriginOperationId, readPendingNoteAction, noteUuid, type NoteActionPrefill } from '@/lib/noteExecutor';
import type { ActionProposal } from '@/types/mirror';
import { mirrorNoteRecoveryLocator } from '@/lib/noteRecovery';

export interface BrainLiveOrigin<T> { args: T; isCurrent: () => boolean }
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, stable((value as Record<string, unknown>)[key])]));
  return value;
}
export const sameBrainProposalValue = (left: unknown, right: unknown) => JSON.stringify(stable(left)) === JSON.stringify(stable(right));

/** The server preview is re-derived from this exact literal turn before it gains a live origin. */
export function prepareCurrentBrainProposal(tool: string, args: unknown, ownerId: string, sessionId: string, literal: string): BrainNoteProposalArgs | BrainHandoffArgs | null {
  const prepared = createMirrorBrainActions({ userId: ownerId, sessionId, channel: 'app', enabled: true, latestUserText: literal }).prepare(tool, { raw_text: literal });
  return prepared && sameBrainProposalValue(prepared, args) ? structuredClone(prepared) : null;
}

/** Historical chips can only recover their original receipt. They never authorize a new write. */
export async function prepareBrainNoteModal(proposal: ActionProposal, options: {
  ownerId: string; sessionId: string; isCurrent: () => boolean; liveOrigin?: BrainLiveOrigin<BrainNoteProposalArgs>;
}): Promise<NoteActionPrefill> {
  if (proposal.tool !== 'propose_brain_note_action' || !options.isCurrent()) throw new Error('invalid_session');
  const raw = proposal.args as unknown as Record<string, unknown> | undefined;
  const literal = typeof raw?.raw_text === 'string' ? raw.raw_text : '';
  const args = prepareCurrentBrainProposal(proposal.tool, proposal.args, options.ownerId, options.sessionId, literal) as BrainNoteProposalArgs | null;
  if (!args || !['create', 'update', 'delete'].includes(args.action) || !['map', 'emotion'].includes(args.brain_kind)) throw new Error('invalid_request');
  if (options.liveOrigin && !sameBrainProposalValue(options.liveOrigin.args, args)) throw new Error('invalid_request');
  const fresh = !!options.liveOrigin?.isCurrent();
  const operationId = await noteOriginOperationId(options.ownerId, options.sessionId, proposal.action_id, 'brain:' + args.brain_kind + ':operation');
  if (!options.isCurrent()) throw new Error('invalid_session');
  let noteId = args.action === 'create'
    ? await noteOriginOperationId(options.ownerId, options.sessionId, proposal.action_id, 'brain:' + args.brain_kind + ':note')
    : args.note_id;
  if (!options.isCurrent()) throw new Error('invalid_session');
  const recoveryOnly = !fresh || !options.liveOrigin?.isCurrent();
  // A reviewed query resolves to a human-selected ID only in its original journal.
  // Historical recovery must never re-run that query or select a different note.
  if (recoveryOnly && !noteId) {
    const entry = await readPendingNoteAction(options.ownerId, args.brain_kind);
    if (!options.isCurrent()) throw new Error('invalid_session');
    const locator = entry && mirrorNoteRecoveryLocator(entry);
    if (!entry || !locator || locator.user_id !== options.ownerId || locator.session_id !== options.sessionId ||
      locator.action_id !== proposal.action_id || locator.operation_id !== operationId || locator.brain_kind !== args.brain_kind ||
      entry.body.action !== args.action || !noteUuid(entry.body.note_id)) throw new Error('invalid_note_origin');
    noteId = entry.body.note_id;
  }
  return { action: args.action, brain_kind: args.brain_kind, operation_id: operationId, action_id: proposal.action_id,
    ...(noteId ? { note_id: noteId } : {}), ...(args.query !== undefined ? { query: args.query } : {}),
    ...(args.content !== undefined ? { content: args.content } : {}), type: 'insight',
    expected_user_id: options.ownerId, expected_session_id: options.sessionId,
    recovery_only: recoveryOnly };
}
